import React from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Ballot,
  CoPresent,
  Task,
  Sort,
  Construction,
  Comment,
  ManageSearch,
} from '@mui/icons-material';
import {
  NonProposedList,
  nonProposedFilter,
  ConditionCheckList,
  PrioritizeEvaluation,
  drcConditionToMeetNumFilter,
  dorpgPolishCommentsFilter,
  DepartmentApplicationList,
} from './components';
import { FunctionHub } from './components/FunctionHub';
import { PAGE_TEXT } from '../lang';
import {
  AssessmentSidebar,
  DRC_ASSESSMENT_MODULE,
  DepartmentScholarshipManagementList,
} from '../components';
import { useAuth } from '../../../contexts/authContext/authContext';
import { type SidebarData } from '../../../components';
import { PolishCommentsList } from '../../Nomination/components';

export const DRCAssessment: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  // am means supervisor SideBar
  let sidebarActivated = searchParams.get('am') as DRC_ASSESSMENT_MODULE;

  if (!Object.values(DRC_ASSESSMENT_MODULE).includes(sidebarActivated)) {
    sidebarActivated = DRC_ASSESSMENT_MODULE.APPLICATION_LIST;
  }

  const [activatedModuleId, setActivatedModuleId] =
    React.useState(sidebarActivated);

  React.useEffect(() => {
    // to keep "fm" module activation in "function hub"
    if (activatedModuleId !== DRC_ASSESSMENT_MODULE.FUNCTION_HUB) {
      // delete unnecessary param
      const keys = Array.from(searchParams.keys());
      keys.forEach(key => {
        if (key !== 'am') {
          searchParams.delete(key);
        }
      });
    }

    searchParams.set('am', activatedModuleId);
    // Update the URL with new Module
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  }, [activatedModuleId, location.pathname]);

  const handleChangeActivated = (activatedId: string): void => {
    setActivatedModuleId(activatedId as DRC_ASSESSMENT_MODULE);
  };

  const { user } = useAuth();

  const sidebarData: SidebarData[] = [
    {
      id: DRC_ASSESSMENT_MODULE.APPLICATION_LIST,
      name: PAGE_TEXT.APPLICATION_LIST.TITLE,
      icon: <Ballot />,
    },
    {
      id: DRC_ASSESSMENT_MODULE.NON_PROPOSED,
      name: PAGE_TEXT.NON_PROPOSED.TITLE,
      icon: <CoPresent />,
    },
    {
      id: DRC_ASSESSMENT_MODULE.MEET_CONDITION_CHECK,
      name: PAGE_TEXT.MEET_CONDITION_CHECK.TITLE,
      icon: <Task />,
    },
    {
      id: DRC_ASSESSMENT_MODULE.PRIORITIZE_EVALUATION,
      name: PAGE_TEXT.PRIORITIZE_EVALUATION.TITLE,
      icon: <Sort />,
    },
    {
      id: DRC_ASSESSMENT_MODULE.FUNCTION_HUB,
      name: PAGE_TEXT.FUNCTION_HUB.TITLE,
      icon: <Construction />,
    },
    {
      id: DRC_ASSESSMENT_MODULE.POLISH_COMMENTS,
      name: PAGE_TEXT.REVIEW_APPLICATION.TITLE,
      icon: <Comment />,
    },
    {
      id: DRC_ASSESSMENT_MODULE.SCHOLARSHIP_MANAGEMENT,
      name: PAGE_TEXT.SCHOLARSHIP_MANAGEMENT.TITLE,
      icon: <ManageSearch />,
    },
  ];

  const assessmentModulesMap: Partial<
    Record<DRC_ASSESSMENT_MODULE, { component: any; moduleTitle: string }>
  > = {
    [DRC_ASSESSMENT_MODULE.APPLICATION_LIST]: {
      component: <DepartmentApplicationList />,
      moduleTitle: PAGE_TEXT.APPLICATION_LIST.TITLE,
    },
    [DRC_ASSESSMENT_MODULE.NON_PROPOSED]: {
      component: <NonProposedList filter={nonProposedFilter(user)} />,
      moduleTitle: PAGE_TEXT.NON_PROPOSED.TITLE,
    },
    [DRC_ASSESSMENT_MODULE.MEET_CONDITION_CHECK]: {
      component: (
        <ConditionCheckList filter={drcConditionToMeetNumFilter(user)} />
      ),
      moduleTitle: PAGE_TEXT.MEET_CONDITION_CHECK.TITLE,
    },
    [DRC_ASSESSMENT_MODULE.PRIORITIZE_EVALUATION]: {
      component: (
        <PrioritizeEvaluation
          viewOnly={false}
          department={user?.userInfo.department}
        />
      ),
      moduleTitle: PAGE_TEXT.PRIORITIZE_EVALUATION.TITLE,
    },
    [DRC_ASSESSMENT_MODULE.FUNCTION_HUB]: {
      component: <FunctionHub departmentLevel />,
      moduleTitle: PAGE_TEXT.FUNCTION_HUB.TITLE,
    },
    [DRC_ASSESSMENT_MODULE.POLISH_COMMENTS]: {
      component: <PolishCommentsList filter={dorpgPolishCommentsFilter} />,
      moduleTitle: PAGE_TEXT.REVIEW_APPLICATION.LIST_NAME,
    },
    [DRC_ASSESSMENT_MODULE.SCHOLARSHIP_MANAGEMENT]: {
      component: <DepartmentScholarshipManagementList />,
      moduleTitle: PAGE_TEXT.SCHOLARSHIP_MANAGEMENT.TITLE,
    },
  };

  const activatedModule:
    | { component: React.ReactNode; moduleTitle: string }
    | undefined = assessmentModulesMap[activatedModuleId];

  const moduleComponent = activatedModule?.component ?? activatedModuleId;
  const moduleTitle = activatedModule?.moduleTitle ?? activatedModuleId;

  return (
    <PageContainer>
      <div className="sidebar-area">
        <AssessmentSidebar
          onChangeActivated={handleChangeActivated}
          currentActivatedModule={activatedModuleId}
          collapsible={true}
          drawerWidth={278}
          drawerInnerWidth={'17rem'}
          sidebarData={sidebarData}
        />
      </div>
      <Container>
        <h1>{moduleTitle}</h1>
        <div className="form-area">{moduleComponent}</div>
      </Container>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  display: flex;
  flex-direction: row;

  .sidebar-area {
    width: min-content;
  }

  h1 {
    margin-top: 2rem;
    font-size: 1.5rem;
    font-weight: 700;
    text-align: center;
    color: #951d1d;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 70%;

  .form-area {
    flex: 0.95;
    margin: 1rem;
  }

  .submit-area {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 0.5rem 1rem;
    margin-top: 2rem;
    button {
      margin: 0 1rem;
    }
  }
`;
